/* ══════════════════════════════════════════════════════════════════
   BUSTAH — the engine
   Scroll-scrubbed film, entrance transforms, slide-in panels,
   accordions and the contact form. Everything below this header is
   independent of the shop: it drives how the page moves, not what it
   sells. The shop runtime lives in bustah-shop.js.
   ══════════════════════════════════════════════════════════════════ */
/* ═══════════════════════════════════════════════════════════════════
   THE FADE DECK
   Sections do not travel any more. Each one is pinned to the same place
   on the screen and stacked on the others; scrolling cross-fades the
   current section out and the next one in, in place. A spacer inside
   #page supplies the scroll distance, so the film scrub, the rail, the
   progress bar and the nav all still read window.scrollY exactly as
   before — they just ask __secTop / __secH where a section sits on that
   virtual track instead of measuring a box that no longer moves.

   Homepage-with-film only: without #stage there is nothing to pin the
   page beside, and every other template is an ordinary scrolling page.
   ═══════════════════════════════════════════════════════════════════ */
(function(){
  var page = document.getElementById('page');
  var stage = document.getElementById('stage');
  if(!page || !stage) return;
  var all = [].slice.call(document.querySelectorAll('#page section[id]'));
  if(all.length < 3) return;

  /* THE FIRST SECTION IS NOT ON THE DECK.
     It scrolls, ordinarily, and carries the second one in behind it —
     that hand-off is a scroll, not a fade. The deck starts at the second
     section, and from there on every section is pinned and cross-fades
     into the next without travelling. */
  var flow = all[0];
  /* The Products section straight after it scrolls too (no fade between
     the first two sections); the deck starts after it. */
  var flows = [flow];
  if(all[1] && all.length > 3 && (all[1].id === 'products' || all[1].hasAttribute('data-flow'))) flows.push(all[1]);
  var lastFlow = flows[flows.length - 1];
  var secs = all.slice(flows.length);
  flows.forEach(function(f){ f.classList.add('deck-flow'); });
  if(flows[1]){
    /* it keeps its place in the page (its pinned-section offset would push
       it down), and a jump to it stops below the fixed header */
    flows[1].style.setProperty('top', 'auto', 'important');
    flows[1].style.setProperty('scroll-margin-top', 'var(--navh,74px)');
  }

  /* A section carrying data-nofit is not squeezed onto one screen: it
     keeps its natural height and scrolls, like a section on any ordinary
     page. The shop uses this — a catalogue scaled down to fit a screen
     is unreadable on a phone, and the scale is remeasured every time the
     grid changes, which reads as the section flickering between sizes.

     Only the FLOWING first section can opt out. The pinned ones are the
     deck: each is fixed to the same screen-sized box and the track's
     arithmetic assumes it, so one of those growing past the screen would
     simply be clipped. Keep the shop first on the homepage. */
  var flowFit = !flow.hasAttribute('data-nofit');
  function fitsFlow(f){ return !f.hasAttribute('data-nofit'); }

  var space = document.createElement('div');
  space.id = 'deckspace';
  page.appendChild(space);

  var on = false, step = 1, base = 0, last = [], queued = false, rt;

  /* where the deck's virtual track begins: the document position at which
     the scrolling first section has just left */
  function baseY(){
    var r = lastFlow.getBoundingClientRect();
    return Math.max(0, Math.round(r.top + window.scrollY + r.height));
  }

  /* the deck needs a usable block of screen to pin a section into —
     phones included, where the stylesheet gives the film a shorter band
     so there is room for one */
  function fits(){
    /* The deck is a cross-fade in place: nothing travels, nothing scales
       under it. That is LESS movement than an ordinary scrolling page,
       not more, so a reduced-motion preference is not a reason to fall
       back to one — only ?motion=off, which is a deliberate request for
       a plain page, switches it off. */
    if(window.__motionOff) return false;
    var cs = getComputedStyle(document.documentElement);
    var navh = parseFloat(cs.getPropertyValue('--navh')) || 74;
    var h = window.innerHeight - navh;
    if(window.innerWidth < 1024){
      var band = Math.min(window.innerWidth * 0.50, window.innerHeight * 0.26);
      h = window.innerHeight - navh - band - 14;
    }
    return h >= 250;
  }

  function layout(){
    var want = fits();
    if(want !== on){
      on = want;
      document.documentElement.classList.toggle('deck', on);
      document.body.classList.toggle('deck-on', on);
      last = [];
      if(window.__fgSet) window.__fgSet(!on);   /* the per-element reveal is the deck's opposite */
      if(!on){
        space.style.height = '0px';
        flows.forEach(function(f){ f.style.height = ''; });
        all.forEach(function(s){
          s.style.opacity = ''; s.style.visibility = ''; s.classList.remove('live');
          delete s.dataset.deckTop;
          var c = s.querySelector(':scope > .wrap');
          if(c) c.style.transform = '';
        });
      }
    }
    if(!on) return;
    step = Math.max(340, Math.round(window.innerHeight * 0.9));
    /* The spacer is NOT collapsed before measuring, though it looks like
       it should be. It sits after the first section in the flow, so it
       cannot affect where that section starts or ends — and setting it
       to zero, even for the moment it takes to measure, shortens the
       document to about a screen. The browser then clamps the scroll
       position to the new maximum, and when the spacer comes back the
       clamp stays: the page ends up parked at the footer with everything
       between skipped. That happens on every relayout — a resize, a
       phone's address bar sliding away, the shop grid changing — so it
       reads as the page constantly throwing you to the bottom. */
    flows.forEach(function(f){ f.style.height = fitsFlow(f) ? (screenBox() + 'px') : ''; });
    /* LEAD holds the deck back so the second section only begins to
       appear as the first is genuinely leaving. Without it a pinned
       section fades up while the scrolling one is still mid-screen, and
       the two are briefly on top of each other. */
    var lead = Math.round(window.innerHeight * 0.45);
    base = baseY() + lead;
    /* The spacer runs to the end of the last section's fade PLUS one
       screen, so the footer only starts rising once the last section has
       gone — otherwise it slides up through a section still on screen. */
    var tall = lead + step * (secs.length - 1) + Math.round(step * 0.62) + window.innerHeight;
    /* Never write a spacer height that is not a real, positive number.
       One bad value here collapses the page to a screen and drops the
       visitor at the footer, which is a hard failure to trace back to a
       measurement. */
    if(isFinite(tall) && tall > 0) space.style.height = tall + 'px';
    secs.forEach(function(s, i){ s.dataset.deckTop = base + i * step; });
  }

  /* Everything in a section lands on one screen: measure the content at
     its natural size and, when it is taller than the pinned area, scale
     it down from the top edge to exactly fill it. */
  var FLOOR = 0.42;   /* below this the type stops being readable */

  /* the one screen a section is fitted into — the same box the
     stylesheet gives the scrolling first section */
  function screenBox(){
    var cs = getComputedStyle(document.documentElement);
    var navh = parseFloat(cs.getPropertyValue('--navh')) || 74;
    return Math.max(240, Math.round(window.innerHeight - navh));
  }

  function clear(c){
    c.style.transform = ''; c.style.width = ''; c.style.maxWidth = '';
    c.style.marginTop = ''; c.style.transformOrigin = '';
  }

  /* Scaling alone would leave the block narrow as well as short, so the
     layout is widened by the same factor first: the grid picks up
     another column, the lines rewrap, and the content is shorter before
     it is scaled — so the scale needed is gentler and the result still
     fills the column edge to edge. Two passes, because changing the
     width changes the height it was derived from. Returns how much is
     still over the available height, so the caller knows whether the
     floor was hit. */
  function scaleInto(c, avail){
    clear(c);
    var need = c.getBoundingClientRect().height;
    if(!(need > avail + 1) || need <= 0) return 0;
    var k = Math.max(FLOOR, avail / need);
    c.style.maxWidth = 'none';
    for(var pass = 0; pass < 2; pass++){
      c.style.width = (100 / k) + '%';
      k = Math.max(FLOOR, Math.min(1, avail / c.getBoundingClientRect().height));
    }
    k = Math.round(k * 1000) / 1000;
    c.style.width = (100 / k) + '%';
    c.style.transformOrigin = 'left top';
    c.style.marginTop = '0';
    c.style.transform = 'scale(' + k + ')';
    return c.getBoundingClientRect().height - avail;
  }

  function fit(){
    /* the scrolling first section is fitted too: it is given exactly one
       screen and its contents are scaled into it, so it never grows a
       second scrollbar of its own and never runs past the fold */
    for(var i = 0; i < all.length; i++){
      var sec = all[i], c = sec.querySelector(':scope > .wrap');
      if(!c) continue;
      clear(c);
      sec.classList.remove('tall');
      var isFlow = flows.indexOf(sec) > -1;
      if(isFlow) sec.style.height = '';
      if(!on) continue;
      /* Opted out: no forced height, no scale. Leave it exactly as the
         stylesheet laid it out. */
      if(isFlow && !fitsFlow(sec)) continue;
      if(isFlow) sec.style.height = screenBox() + 'px';

      var cs = getComputedStyle(sec);
      var avail = sec.clientHeight - parseFloat(cs.paddingTop) - parseFloat(cs.paddingBottom);
      if(avail <= 0) continue;

      /* Over even at the floor. The old answer was a scrollbar inside the
         section — that is the double scroll: the page scrolls AND the
         section scrolls, and the wheel does one or the other depending on
         where the pointer happens to be. Instead the section is marked
         .tall, which the stylesheet answers by tightening that one
         section's own layout, and it is measured and scaled again against
         the tighter rules. Nothing is hidden, and there is still only one
         scrollbar on the page. */
      if(scaleInto(c, avail) > 1){
        sec.classList.add('tall');
        scaleInto(c, avail);
      }
    }
  }

  function paint(){
    queued = false;
    if(!on) return;
    /* the track starts where the scrolling first section ends, so the
       second section is fully in exactly as that one finishes leaving */
    var u = (window.scrollY - base) / step, best = 0, bo = -1, i, o, d;
    for(i = 0; i < secs.length; i++){
      d = Math.abs(u - i);
      o = d <= 0.30 ? 1 : d >= 0.62 ? 0 : 1 - (d - 0.30) / 0.32;
      o = Math.round(o * 100) / 100;
      if(o !== last[i]){
        last[i] = o;
        secs[i].style.opacity = o;
        secs[i].style.visibility = o <= 0 ? 'hidden' : '';
      }
      if(o > bo){ bo = o; best = i; }
    }
    for(i = 0; i < secs.length; i++) secs[i].classList.toggle('live', i === best && bo > 0.5);
  }
  function onScroll(){ if(queued) return; queued = true; requestAnimationFrame(paint); }

  /* where a section sits on the virtual track, for everything that used
     to measure its box */
  window.__secTop = function(el){
    if(on && el.dataset && el.dataset.deckTop !== undefined) return +el.dataset.deckTop;
    return el.getBoundingClientRect().top + window.pageYOffset;
  };
  window.__secH = function(el){
    if(on && el.dataset && el.dataset.deckTop !== undefined) return step;
    return el.getBoundingClientRect().height;
  };
  window.__goto = function(el){
    if(!el) return;
    if(on && el.dataset && el.dataset.deckTop !== undefined)
      window.scrollTo({ top: +el.dataset.deckTop, behavior: 'smooth' });
    else el.scrollIntoView({ behavior: 'smooth' });
  };
  window.__deckOn = function(){ return on; };

  /* a link to a section is a jump along the virtual track, not to a box */
  document.addEventListener('click', function(e){
    if(!on) return;
    var a = e.target.closest && e.target.closest('a[href^="#"]');
    if(!a || a.hasAttribute('data-panel') || a.hasAttribute('data-close')) return;
    var href = a.getAttribute('href');
    if(!href || href.length < 2) return;
    var t = document.getElementById(href.slice(1));
    if(!t || t.dataset.deckTop === undefined) return;
    e.preventDefault();
    window.__goto(t);
  }, true);

  function refresh(){
    /* A relayout can shorten the document for an instant — a section
       being remeasured, the spacer being rewritten — and the browser
       answers by clamping the scroll position to whatever the maximum is
       at that moment. The clamp is not undone when the height comes
       back, so the reader is quietly moved. Put them back where they
       were, but only if they did not scroll themselves in between. */
    var y = window.scrollY;
    layout(); fit(); paint();
    if(window.scrollY !== y && Math.abs(window.scrollY - y) > 2 &&
       document.documentElement.scrollHeight - window.innerHeight >= y){
      window.scrollTo(0, y);
      paint();
    }
    if(window.__rebuildFilm)   window.__rebuildFilm();
    if(window.__remeasureNav)  window.__remeasureNav();
    if(window.__remeasureRail) window.__remeasureRail();
  }
  layout(); fit(); paint();
  window.addEventListener('scroll', onScroll, {passive:true});
  window.addEventListener('resize', function(){ clearTimeout(rt); rt = setTimeout(refresh, 150); });
  window.addEventListener('orientationchange', function(){ setTimeout(refresh, 260); });
  window.addEventListener('load', refresh);
  if(document.fonts && document.fonts.ready) document.fonts.ready.then(refresh);
  /* a late image, or a grid swapped in by a category tab, changes how
     tall a section is */
  document.querySelectorAll('#page img').forEach(function(im){
    if(!im.complete) im.addEventListener('load', function(){
      clearTimeout(rt); rt = setTimeout(refresh, 120);
    }, {once:true});
  });
  window.__refreshDeck = refresh;
  window.__refitDeck = fit;
})();

/* ═══════════════════════════════════════════════════════════════════
   BUSTAH — the film hold
   The first section on the page owns the whole screen: no film beside
   it, no reserved column. The film only arrives once that section has
   started to go, and scrubs from its first frame through everything
   after it. Which section that is comes from the page rather than from
   a name here — reorder the homepage in the theme editor and the hold
   follows whatever ends up first.
   ═══════════════════════════════════════════════════════════════════ */
(function(){
  if(!document.getElementById('stage')) return;
  var first = document.querySelector('#page section[id]');
  if(!first) return;
  first.classList.add('film-free');

  var held = null, rt;

  /* The scroll position the hold lets go at. The film scrubber anchors
     its first frame here, so the footage starts at 0 exactly as it
     appears rather than having already run on behind the hold. */
  /* The first section scrolls rather than sitting on the deck, in both
     modes, so its own box is the measure in both: the film is held until
     that section is all but gone, then fades in over the last of it. */
  function releaseY(){
    var r = first.getBoundingClientRect();
    return Math.max(0, Math.round(window.scrollY + r.bottom - window.innerHeight * 0.18));
  }
  window.__filmHoldEnd = releaseY;

  function want(){ return window.scrollY < releaseY(); }

  function apply(){
    var h = want();
    if(h === held) return;
    held = h;
    document.documentElement.classList.toggle('film-hold', h);
    document.body.classList.toggle('film-hold', h);
    /* the section changes width when the column is handed back, so what
       the deck scaled it to no longer fits — remeasure once the
       transition has landed */
    clearTimeout(rt);
    rt = setTimeout(function(){ if(window.__refitDeck) window.__refitDeck(); }, 620);
  }

  apply();
  /* the scrub curve is built from this module's release point, and the
     film module was parsed before it existed */
  if(window.__rebuildFilm) window.__rebuildFilm();
  var q = false;
  window.addEventListener('scroll', function(){
    if(q) return; q = true;
    requestAnimationFrame(function(){ q = false; apply(); });
  }, {passive:true});
  window.addEventListener('resize', apply);
  window.addEventListener('load', apply);
})();

/* ═══════════════════════════════════════════════════════════════════
   BUSTAH — the cross-fade fallback
   The deck above already cross-fades sections in place, and it is what
   normally runs. When it cannot — a viewport too short to pin a section
   into, ?motion=off — the page falls back to ordinary scrolling, where
   a section arrives from below and leaves at the top. This gives that
   fallback the same behaviour the deck has: every section past the
   first fades up as it comes to the middle of the screen and fades back
   out as it leaves, driven by scroll position rather than a timer.

   It writes opacity to the SECTION, which nothing else writes to — the
   per-element reveal writes to the blocks inside it — so the two sit on
   top of each other rather than fighting over the same property.
   ═══════════════════════════════════════════════════════════════════ */
(function(){
  var secs = [].slice.call(document.querySelectorAll('#page section[id]'));
  if(secs.length < 2) return;
  secs = secs.slice(1).filter(function(s){ return !s.classList.contains('deck-flow'); });   /* the scrolling sections are left alone */

  var last = [], queued = false, wasDeck = null;

  /* full while the section owns the middle of the screen, gone by the
     time its centre is most of a screen away from it */
  var HOLD = 0.34, FADE = 0.34;

  function paint(){
    queued = false;
    var deck = !!(window.__deckOn && window.__deckOn());
    if(deck !== wasDeck){
      wasDeck = deck;
      if(deck){                  /* the deck owns opacity from here */
        secs.forEach(function(s, i){ s.style.opacity = ''; s.style.visibility = ''; last[i] = -1; });
      }
    }
    if(deck) return;

    var vh = window.innerHeight;
    for(var i = 0; i < secs.length; i++){
      var r = secs[i].getBoundingClientRect();
      if(r.height <= 0) continue;
      var d = Math.abs((r.top + r.height / 2) - vh / 2) / ((r.height + vh) / 2);
      var o = d <= HOLD ? 1 : d >= HOLD + FADE ? 0 : 1 - (d - HOLD) / FADE;
      o = Math.round(o * 100) / 100;
      if(o !== last[i]){
        last[i] = o;
        secs[i].style.opacity = o;
        secs[i].style.visibility = o <= 0 ? 'hidden' : '';
      }
    }
  }
  function onScroll(){ if(queued) return; queued = true; requestAnimationFrame(paint); }

  paint();
  window.addEventListener('scroll', onScroll, {passive:true});
  window.addEventListener('resize', onScroll);
  window.addEventListener('load', onScroll);
})();

/* ═══════════════════════════════════════════════════════════════════
   BUSTAH — scroll-scrubbed film
   The background film does not autoplay. Its playhead is driven purely
   by scroll position, and anchored so each section lands on its own
   moment in the footage (field → mill → docks → sea → herd).
   ═══════════════════════════════════════════════════════════════════ */
(function(){
  var v = document.getElementById('bgv');
  if(!v) return;
  var reduce = !window.__motion;

  var pts = [], dur = 0, target = 0, cur = 0, ready = false, raf = null, primed = false;
  /* The film is 24 fps and encoded with every frame a keyframe, which is
     what makes a scrub smooth: a normal encode keeps one keyframe every
     few seconds, so seeking to an arbitrary frame means decoding every
     frame since the last one — dozens of frames of work for one step of
     the wheel, which is felt as stutter no matter how the playhead is
     eased. All-keyframe costs file size and buys instant seeks.

     Seeking finer than one frame just re-decodes the same picture, so the
     playhead is still snapped to frame boundaries and skipped entirely
     while a previous seek is in flight. */
  var FRAME = 1 / 24, shown = -1;

  function docH(){ return Math.max(1, document.documentElement.scrollHeight - window.innerHeight); }

  /* Build the scroll→time curve from the [data-vt] anchors on the page. */
  function build(){
    dur = (isFinite(v.duration) && v.duration > 0) ? v.duration : 10;
    var list = [];
    document.querySelectorAll('[data-vt]').forEach(function(el){
      var t0 = window.__secTop ? window.__secTop(el) : el.getBoundingClientRect().top + window.scrollY;
      var h0 = window.__secH   ? window.__secH(el)   : el.getBoundingClientRect().height;
      var mid = t0 + h0/2 - window.innerHeight/2;
      list.push({ y: Math.max(0, Math.min(docH(), mid)), t: parseFloat(el.dataset.vt) });
    });
    /* The first section runs without the film (see "the film hold"): the
       stage is faded out over it, so any footage the curve would spend
       there is footage nobody sees — and the film would then arrive
       already part-way through its first move. Pinning t=0 to the scroll
       position the hold lets go at makes the first frame the frame you
       see the moment the film appears. */
    if(window.__filmHoldEnd){
      var hy = window.__filmHoldEnd();
      if(isFinite(hy) && hy > 0) list.push({ y: Math.min(docH(), hy), t: 0 });
    }
    list.sort(function(a,b){ return a.y - b.y; });
    pts = [{y:0, t:0}].concat(list, [{y: docH(), t: dur}]);
    /* keep it monotonic so the film never runs backwards while scrolling down */
    for(var i=1;i<pts.length;i++){
      if(pts[i].y <= pts[i-1].y) pts[i].y = pts[i-1].y + 1;
      if(pts[i].t <  pts[i-1].t) pts[i].t = pts[i-1].t;
    }
  }

  function timeAt(y){
    if(!pts.length) return 0;
    if(y <= pts[0].y) return pts[0].t;
    for(var i=1;i<pts.length;i++){
      if(y <= pts[i].y){
        var a = pts[i-1], b = pts[i];
        var k = (y - a.y) / (b.y - a.y);
        k = k*k*(3-2*k);                 /* ease inside each leg */
        return a.t + (b.t - a.t) * k;
      }
    }
    return pts[pts.length-1].t;
  }

  function tick(){
    raf = null;
    var d = target - cur;
    /* Chase the target a fifth of the way each frame. Slower than this
       and the film visibly lags the scroll; faster and it arrives in one
       jump, which is the stutter the easing exists to remove. */
    if(Math.abs(d) < 0.002){ cur = target; }
    else { cur += d * 0.20; raf = requestAnimationFrame(tick); }
    window.__t = cur;      /* the computed playhead, for testing */
    if(ready && !v.seeking){
      var f = Math.round(cur / FRAME) * FRAME;
      if(f !== shown){
        shown = f;
        try{ v.currentTime = Math.max(0, Math.min(dur - FRAME, f)); }catch(e){}
      }
    }
  }

  function onScroll(){
    target = timeAt(window.scrollY);
    if(reduce){ cur = target; window.__t = cur;
      if(ready && !v.seeking){ shown = cur; try{ v.currentTime = cur; }catch(e){} } return; }
    if(!raf) raf = requestAnimationFrame(tick);
  }

  /* iOS/Safari will not paint a frame until the element has been played once. */
  function prime(){
    if(primed) return;
    /* Nothing to prime until the film has a source. Spending the one
       prime on an empty element meant iOS never got its first frame. */
    if(!v.currentSrc && !v.getAttribute('src')){ if(window.__filmGo) window.__filmGo(); return; }
    primed = true;
    var p = v.play();
    if(p && p.then) p.then(function(){ v.pause(); }).catch(function(){});
    else { try{ v.pause(); }catch(e){} }
  }

  function start(){
    ready = true;
    shown = -1;
    build(); onScroll();
    document.getElementById('stage').classList.add('live');
  }

  if(v.readyState >= 1) start();
  v.addEventListener('loadedmetadata', start);
  v.addEventListener('canplay', function(){ build(); onScroll(); });
  v.addEventListener('play', function(){ if(primed) v.pause(); });
  v.addEventListener('loadeddata', prime);

  /* A seek requested while an earlier one is still in flight is skipped
     (see tick). If the chase settles during that time — which is exactly
     what happens on the first scroll, when the frames are still coming
     over the network — the last position was never applied and the film
     sat on a stale frame until the next scroll. Once a seek lands, catch
     up to wherever the playhead should be now. */
  v.addEventListener('seeked', function(){
    if(Math.round(cur / FRAME) * FRAME !== shown && !raf) raf = requestAnimationFrame(tick);
  });
  /* priming plays for an instant, which moves the playhead off the frame
     the scroll asked for */
  v.addEventListener('pause', function(){ shown = -1; if(ready && !raf) raf = requestAnimationFrame(tick); });

  /* The page keeps changing height after first paint — product photos
     arriving in the shop grid, fonts, a category switch — and every
     change moves where each section sits on the scroll track. Rebuild
     the scrub curve whenever the document height actually changes, so a
     first scroll never runs on anchors measured for a shorter page. */
  if(window.ResizeObserver){
    var lastDocH = 0, rot;
    new ResizeObserver(function(){
      var h = document.documentElement.scrollHeight;
      if(h === lastDocH) return;
      lastDocH = h;
      clearTimeout(rot); rot = setTimeout(function(){ build(); onScroll(); }, 120);
    }).observe(document.body);
  }

  window.addEventListener('scroll', onScroll, {passive:true});
  ['touchstart','pointerdown','wheel','keydown'].forEach(function(ev){
    window.addEventListener(ev, prime, {once:true, passive:true});
  });
  setTimeout(prime, 900);

  var rt;
  window.addEventListener('resize', function(){
    clearTimeout(rt); rt = setTimeout(function(){ build(); onScroll(); }, 160);
  });
  window.addEventListener('load', function(){ build(); onScroll(); });
  window.__rebuildFilm = function(){ build(); onScroll(); };
})();

/* ═══════════ JOURNEY RAIL ═══════════
   Built from the sections that are actually on the page rather than from
   a menu, so it is always in step with the page. A section earns a step
   by having an id and an eyebrow (.tag) — which is every section on the
   homepage except the hero, and the hero is where the rail starts from,
   not a stop on it. */
(function(){
  var rail = document.querySelector('[data-rail]');
  if(!rail || rail.children.length) return;
  var secs = [].slice.call(document.querySelectorAll('#page section[id]'));
  var html = '';
  secs.forEach(function(sec){
    var tag = sec.querySelector('.tag');
    if(!tag) return;
    var label = (tag.textContent || '').trim();
    if(!label) return;
    if(html) html += '<div class="r-line"><b></b></div>';
    html += '<a class="r-step" href="#' + sec.id + '" tabindex="-1">'
          +   '<span class="r-dot"></span><span class="r-lab">' + label + '</span>'
          + '</a>';
  });
  if(html) rail.innerHTML = html; else rail.remove();
})();

/* ═══════════ NAV ═══════════ */
(function(){
  var nav = document.querySelector('nav'), burger = document.getElementById('burger'),
      mnav = document.getElementById('mnav'), prog = document.getElementById('prog'),
      top  = document.getElementById('top'), rail = document.getElementById('rail');

  /* In a theme every one of these can legitimately be absent — a merchant
     removes a section, turns the film off, empties a menu. Before, a
     missing #rail threw inside frame() on every scroll frame, which took
     the progress bar, the back-to-top button and the active nav link down
     with it. Nothing below assumes an element exists. */
  if(burger && mnav){
    burger.addEventListener('click', function(){
      var open = mnav.classList.toggle('open');
      burger.classList.toggle('x', open);
      burger.setAttribute('aria-expanded', open ? 'true' : 'false');
    });
    mnav.querySelectorAll('a').forEach(function(a){
      a.addEventListener('click', function(){
        burger.classList.remove('x'); mnav.classList.remove('open');
        burger.setAttribute('aria-expanded', 'false');
      });
    });
  }

  var secs = [].slice.call(document.querySelectorAll('section[id]'));
  var links = [].slice.call(document.querySelectorAll('nav ul a[href^="#"]'));
  /* Offsets are cached: reading them inside the scroll handler forces the
     browser to re-lay-out the whole page on every frame. */
  var tops = [], docH = 1, vh = 1, lastId = null;
  function measure(){
    tops = secs.map(function(s){ return window.__secTop ? window.__secTop(s) : s.offsetTop; });
    docH = Math.max(1, document.documentElement.scrollHeight - window.innerHeight);
    vh   = window.innerHeight;
  }

  function frame(){
    var y = window.scrollY;
    if(nav)  nav.classList.toggle('stuck', y > 40);
    if(prog) prog.style.width = (y / docH * 100) + '%';
    if(top)  top.classList.toggle('on', y > 520);
    if(rail) rail.classList.toggle('on', y > vh * 0.8 && y < docH - 400);

    var cur = '';
    for(var i = 0; i < tops.length; i++) if(y >= tops[i] - 130) cur = secs[i].id;
    if(cur !== lastId){
      lastId = cur;
      links.forEach(function(a){ a.classList.toggle('act', a.getAttribute('href') === '#' + cur); });
    }
  }
  var t = false;
  window.addEventListener('scroll', function(){
    if(t) return; t = true;
    requestAnimationFrame(function(){ frame(); t = false; });
  }, {passive:true});
  var mt;
  window.addEventListener('resize', function(){
    clearTimeout(mt); mt = setTimeout(function(){ measure(); frame(); }, 150);
  });
  window.addEventListener('load', function(){ measure(); frame(); });
  measure(); frame();
  if(top) top.addEventListener('click', function(){
    window.scrollTo({top:0, behavior:'smooth'});
  });
  window.__remeasureNav = function(){ measure(); frame(); };
})();

/* ═══════════ RAIL PROGRESS ═══════════ */
(function(){
  var steps = [].slice.call(document.querySelectorAll('.r-step'));
  var lines = [].slice.call(document.querySelectorAll('.r-line b'));
  if(!steps.length) return;
  var marks = steps.map(function(a){
    return document.querySelector(a.getAttribute('href'));
  }).filter(Boolean);
  if(marks.length !== steps.length) return;

  var box = [], lastActive = -2;
  function measure(){
    box = marks.map(function(m){
      return [ window.__secTop ? window.__secTop(m) : m.offsetTop,
               window.__secH   ? window.__secH(m)   : m.offsetHeight ]; });
  }
  function upd(){
    var mid = window.scrollY + window.innerHeight * 0.5, active = -1;
    for(var i = 0; i < box.length; i++){
      var top = box[i][0], h = box[i][1];
      if(mid >= top) active = i;
      if(lines[i]) lines[i].style.height =
        Math.max(0, Math.min(1, (mid - top) / Math.max(1, h))) * 100 + '%';
    }
    if(active === lastActive) return;
    lastActive = active;
    steps.forEach(function(s, i){
      s.classList.toggle('now', i === active);
      s.classList.toggle('done', i < active);
    });
  }
  var t = false;
  window.addEventListener('scroll', function(){
    if(t) return; t = true;
    requestAnimationFrame(function(){ upd(); t = false; });
  }, {passive:true});
  var rt2;
  window.addEventListener('resize', function(){
    clearTimeout(rt2); rt2 = setTimeout(function(){ measure(); upd(); }, 150);
  });
  window.addEventListener('load', function(){ measure(); upd(); });
  measure(); upd();
  window.__remeasureRail = function(){ measure(); upd(); };
})();

/* ═══════════ SCROLL-LINKED FOREGROUND ═══════════
   The aim is not to animate the content — it is to stop it reading as a
   layer on top of the film and make it part of the picture.

   So nothing slides. A block dissolves up where it already is, the way a
   title appears in a shot, and every section is given the same slow push
   the camera gives the footage. With the grain and the vignette falling
   across the type as well (see #lens in the stylesheet), the two read as
   one image rather than as text over video.

   All of it is a function of scroll position, never of a timer, so it runs
   backwards when you scroll back, in step with the footage.

   Geometry is measured once and cached — never inside the scroll handler,
   which would force a full re-layout on every frame — and an element is
   only written to when its progress has actually moved. */
(function(){
  var root = document.documentElement;
  if(!/(^| )js( |$)/.test(root.className)) root.className += ' js';
  if(!window.__motion) return;

  var items = [], drifts = [], vh = 1, lastW = 0, queued = false;
  /* The deck (top of this file) fades whole sections instead, and its
     fit-to-screen scale lives on the very element the camera push writes
     to — so the two cannot both run. __fgSet(false) stands this module
     down and clears what it wrote; the deck calls it whenever it turns
     itself on or off. */
  var enabled = !(window.__deckOn && window.__deckOn());

  /* A block dissolves up as it crosses the lower part of the screen —
     starting as its top passes the bottom edge (LEAD) and fully there a
     little under half a screen later (SPAN). STEP staggers the items of a
     row, in scroll distance, not time. */
  var LEAD = 1.10, SPAN = 0.42, STEP = 0.05;

  /* WHICH WAY CONTENT ARRIVES
     Scrolling already carries everything up the screen, so a block that
     also rises as it enters is arriving on the one axis the page is
     already using — it reads as sliding up, whatever the timing. These
     bring it in on a different axis instead. Add ?enter=<name> to the URL
     to try another; 'depth' is the default.

       depth     it comes toward you out of the picture, growing into
                 place — the same axis the camera dollies along
       sides     it converges inward: blocks left of centre come from the
                 left, blocks right of centre from the right
       down      it settles downward from above, against the scroll
       dissolve  no movement at all, just a fade
  */
  var ENTER = (/[?&]enter=(depth|sides|down|dissolve)/.exec(location.search) || [,'depth'])[1];
  var DEPTH = 0.14, SIDE = 90, DOWN = 70;
  /* PUSH is the camera move: a section grows by this much while it crosses
     the screen, matching the way the footage dollies forward. It replaces
     the vertical drift, which was the very thing that made the content look
     like it was sliding over the film rather than sitting inside it.
     PHOTO is the same idea for a photograph inside its frame. */
  var PUSH = 0.045, PHOTO = 18;

  function ease(t){ return t <= 0 ? 0 : t >= 1 ? 1 : t * t * (3 - 2 * t); }

  function collect(){
    items = []; drifts = [];
    /* .rvl and .rvr used to mean "slide in from the left / right". Nothing
       slides any more, so all three are the same dissolve; the classes stay
       in the markup because they still say which side a block sits on. */
    document.querySelectorAll('#page .rv,#page .rvl,#page .rvr').forEach(function(el){
      items.push({ el: el, i: 0, p: -1 });
    });
    document.querySelectorAll('#page .stg').forEach(function(g){
      for(var i = 0; i < g.children.length; i++)
        items.push({ el: g.children[i], dx: 0, i: i, n: i, p: -1 });
    });
    document.querySelectorAll('#page .nbar').forEach(function(bar){
      items.push({ el: bar, bar: +bar.getAttribute('data-w'), i: 0, p: -1 });
    });
    /* Not '#page > section' — Shopify wraps every section in its own
       <div id="shopify-section-…">, so the direct-child selector this was
       written with matches nothing in a theme and the camera push silently
       never applies. A descendant selector survives the wrapper. */
    document.querySelectorAll('#page section > .wrap, footer > .ftop').forEach(function(el){
      el.classList.add('drift');
      drifts.push({ el: el, p: -1 });
    });
    document.querySelectorAll('#page .bimg img').forEach(function(im){
      im.classList.add('para');
      drifts.push({ el: im, p: -1, pc: 1 });
    });
  }

  function measure(){
    vh = window.innerHeight; lastW = window.innerWidth;
    var i, r;

    /* Every position below is read with the animation cleared. Measuring
       while a section's push is still applied reports its contents away
       from where they really sit, by a different amount for each one.
       update() runs straight after and repaints. */
    for(i = 0; i < items.length; i++) items[i].p = -1;
    for(i = 0; i < drifts.length; i++){ drifts[i].el.style.transform = ''; drifts[i].p = -1; }

    var mid = window.innerWidth / 2;
    for(i = 0; i < items.length; i++){
      var r0 = items[i].el.getBoundingClientRect();
      items[i].top = r0.top + window.pageYOffset;
      /* which side of the screen it sits on, for the 'sides' entrance */
      items[i].side = items[i].el.classList.contains('rvl') ? -1
                    : items[i].el.classList.contains('rvr') ?  1
                    : (r0.left + r0.width / 2) < mid ? -1 : 1;
    }
    /* A row that has wrapped into a column no longer needs an index
       stagger: its items already arrive one after another because they are
       at different heights. Staggering those as well makes the lower ones
       turn up far too late. */
    for(i = 0; i < items.length; i++){
      var it = items[i];
      if(it.n === undefined) continue;
      var prev = i > 0 ? items[i - 1] : null;
      it.i = (it.n > 0 && prev && Math.abs(prev.top - it.top) > 8) ? 0 : it.n;
    }
    for(i = 0; i < drifts.length; i++){
      r = drifts[i].el.getBoundingClientRect();
      drifts[i].top = r.top + window.pageYOffset;
      drifts[i].h = r.height;
      if(drifts[i].pc) drifts[i].el.style.transform = 'scale(1.26)';
    }
  }

  function update(){
    queued = false;
    if(!enabled) return;
    var y = window.pageYOffset, i, it, p;

    for(i = 0; i < items.length; i++){
      it = items[i];
      var start = it.top - vh * LEAD + (it.i > 5 ? 5 : it.i) * vh * STEP;
      p = ease((y - start) / (vh * SPAN));
      p = Math.round(p * 100) / 100;         /* 100 steps is past the eye's reach */
      if(p === it.p) continue;
      it.p = p;
      if(it.bar){
        it.el.style.width = (it.bar * p).toFixed(1) + '%';
      } else {
        /* Promote only while it is actually moving. On its own layer the
           browser composites the change; without one it repaints the text,
           and text with a shadow is the most expensive thing here to
           repaint. */
        /* Promote only while it is actually dissolving. On its own layer
           the browser composites the change; without one it repaints the
           text, and text with a shadow is the most expensive thing here to
           repaint. */
        var moving = p > 0 && p < 1;
        if(moving !== it.m){ it.m = moving; it.el.style.willChange = moving ? 'transform,opacity' : ''; }
        var q = 1 - p;
        it.el.style.opacity = p;
        it.el.style.transform =
            ENTER === 'sides' ? 'translate3d(' + (q * SIDE * it.side).toFixed(1) + 'px,0,0)'
          : ENTER === 'down'  ? 'translate3d(0,' + (-q * DOWN).toFixed(1) + 'px,0)'
          : ENTER === 'dissolve' ? 'none'
          :                     'scale(' + (1 - q * DEPTH).toFixed(4) + ')';
      }
    }

    for(i = 0; i < drifts.length; i++){
      it = drifts[i];
      /* 0 as the block's top reaches the bottom of the screen, 1 as its
         bottom leaves the top — a continuous ride across the viewport */
      p = (y + vh - it.top) / (it.h + vh);
      p = p < 0 ? 0 : p > 1 ? 1 : p;
      p = Math.round(p * 200) / 200;
      if(p === it.p) continue;
      it.p = p;
      it.el.style.transform = it.pc
        /* the photograph pans inside its own frame */
        ? 'translate3d(0,' + ((p - 0.5) * PHOTO).toFixed(2) + '%,0) scale(1.26)'
        /* the section is pushed by the same camera that moves the footage */
        : 'scale(' + (1 + p * PUSH).toFixed(4) + ')';
    }
  }

  function onScroll(){ if(queued) return; queued = true; requestAnimationFrame(update); }
  /* measure() clears the inline transform on every '#page section > .wrap'
     — which in deck mode is where the fit-to-screen scale lives. So while
     the deck has this module stood down, it must not measure either. */
  function refresh(){ if(!enabled) return; measure(); update(); }

  collect(); refresh();
  window.addEventListener('scroll', onScroll, {passive:true});

  var rt;
  function debounced(){ clearTimeout(rt); rt = setTimeout(refresh, 150); }
  window.addEventListener('resize', function(){
    /* a phone's address bar and its keyboard both change the height alone;
       re-laying the page out under the visitor for that is worse than not */
    if(window.innerWidth === lastW) return;
    debounced();
  });
  window.addEventListener('orientationchange', function(){ setTimeout(refresh, 260); });
  window.addEventListener('load', refresh);
  if(document.fonts && document.fonts.ready) document.fonts.ready.then(refresh);
  document.querySelectorAll('#page img').forEach(function(im){
    if(!im.complete) im.addEventListener('load', debounced, {once:true});
  });
  /* opening an accordion row moves everything below it */
  /* Anything that changes a section's height has to reach the deck too:
     inside it, height decides the fit-to-screen scale. */
  window.__remeasure = function(){
    refresh();
    if(window.__refreshDeck) window.__refreshDeck();
  };

  /* Rescan, for content that arrives after load.

     collect() snapshots the elements to animate once. Anything injected
     later — a category tab swapping the product grid, Show more adding a
     page — is not in that list, so nothing ever raises it from the
     opacity: 0 the stylesheet starts it at. The cards are in the DOM,
     correct in every way, and invisible. The grid looks empty.

     __remeasure is not enough: it re-measures the elements already
     known. Anything that changes WHICH elements exist has to call this. */
  window.__rescan = function(){ collect(); refresh(); };

  window.__fgSet = function(want){
    if(want === enabled) return;
    enabled = want;
    if(!enabled){
      items.forEach(function(it){
        it.el.style.opacity = ''; it.el.style.transform = ''; it.el.style.willChange = '';
        it.p = -1; it.m = false;
      });
      drifts.forEach(function(d){ d.el.style.transform = ''; d.p = -1; });
    } else refresh();
  };
})();

/* ═══════════ PANELS ═══════════
   The page shows headings only; every piece of detail lives in a panel that
   slides in from the right. One is open at a time. */
(function(){
  var scrim = document.getElementById('scrim');
  if(!scrim) return;
  var open = null, lastFocus = null;

  /* Every panel is moved to be a direct child of <body> before anything
     else happens.

     #page is position:relative with z-index:2, which makes it a stacking
     context — so a panel rendered inside it can never rise above the nav
     (z-index 1000) or the floating pair (900), however high its own
     z-index is. In the original this was free, because the panels were
     written straight into the body. In a theme they arrive inside
     whichever section declared them, which is inside #page.

     Relocating them also makes `position: fixed` safe for good: a panel
     is no longer at the mercy of an ancestor picking up a transform. */
  function hoistPanels(root){
    [].slice.call((root || document).querySelectorAll('.panel')).forEach(function(pan){
      if(pan.parentElement === document.body) return;
      /* The theme editor re-renders a section in place when it is edited,
         which brings a fresh copy of that section's panel back inside
         #page — with the same id as the one already hoisted. Drop the old
         one first, or getElementById starts returning the buried copy. */
      var twin = document.getElementById(pan.id);
      if(twin && twin !== pan && twin.parentElement === document.body) twin.remove();
      document.body.appendChild(pan);
    });
  }
  hoistPanels();
  document.addEventListener('shopify:section:load', function(e){ hoistPanels(e.target); });

  /* ═══ THE BACK BUTTON ═══
     On a phone an overlay that the back gesture does not close is the
     single most reliable way to lose a visitor: they swipe back expecting
     the panel to go away and leave the shop entirely, usually without
     realising they could have stayed.

     So opening a panel pushes one history entry. Back pops it, which
     closes the panel and leaves the visitor exactly where they were.
     Closing the panel any other way — the X, the scrim, Escape — unwinds
     that entry with history.back(), so the stack never accumulates a
     phantom step per panel opened.

     One entry for the whole session of panels, not one per panel: opening
     the bag from inside a product popup should not need two presses of
     back to get out of. */
  var pushed = false;

  function markOpen(){
    if(pushed) return;
    try{
      history.pushState({ bustahPanel: 1 }, '', location.href);
      pushed = true;
    }catch(e){}
  }

  /* fromPop: the browser has already moved back for us, so do not move
     again — doing so would take the visitor off the page, which is the
     exact bug this exists to fix. */
  function close(fromPop){
    if(!open){ if(fromPop) pushed = false; return; }
    var was = open;
    was.classList.remove('open');
    was.setAttribute('aria-hidden', 'true');
    setTimeout(function(){ if(!was.classList.contains('open')) was.classList.remove('live'); }, 520);
    open = null;
    document.body.classList.remove('panel-on');
    scrim.classList.remove('on');
    setTimeout(function(){ if(!open) scrim.hidden = true; }, 420);
    document.body.style.overflow = '';
    if(lastFocus && lastFocus.focus) lastFocus.focus();
    /* Announced rather than assumed. The shop runtime rewrites the URL
       when a product popup opens; it needs to know about every close —
       the X, the scrim and Escape alike — and listening for each of those
       separately is how one of them ends up forgotten. */
    document.dispatchEvent(new CustomEvent('bustah:panelclose', { detail: was }));

    if(pushed){
      pushed = false;
      if(!fromPop){ try{ history.back(); }catch(err){} }
    }
  }

  window.addEventListener('popstate', function(){
    if(open) close(true);
    else pushed = false;
  });

  function show(id, trigger){
    var p = document.getElementById(id);
    if(!p) return;
    if(open && open !== p){
      var prev = open;
      prev.classList.remove('open'); prev.classList.remove('live');
      /* Swapping one panel for another closes the first, and the shop
         runtime has to hear about it — otherwise pressing Buy now inside
         a product popup leaves the URL pointing at that product while the
         bag is what is on screen. */
      document.dispatchEvent(new CustomEvent('bustah:panelclose', { detail: prev }));
    }
    lastFocus = trigger || null;
    scrim.hidden = false;
    p.classList.add('live');
    /* one frame, so the scrim and the panel have a state to transition from */
    requestAnimationFrame(function(){
      scrim.classList.add('on');
      p.classList.add('open');
    });
    p.removeAttribute('aria-hidden');
    open = p;
    document.body.classList.add('panel-on');
    markOpen();
    document.body.style.overflow = 'hidden';
    var x = p.querySelector('.p-x');
    if(x) setTimeout(function(){ x.focus(); }, 120);
  }

  document.addEventListener('click', function(e){
    var t = e.target.closest('[data-panel]');
    if(t){
      /* Every panel trigger in this theme is a real <a href> to a real
         page — that is what makes the catalogue crawlable. So a modified
         click has to be left alone: cmd- or ctrl-clicking a collection
         row should open that collection in a new tab, exactly as the
         markup promises. Only a plain left click becomes a panel. */
      if(t.tagName === 'A' && (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button !== 0)) return;
      /* If the named panel is not in the document — a section switched
         off in the editor, a block pointing at something that no longer
         exists — do not swallow the click. An <a> still has a real page
         behind it, and a trigger that silently does nothing is the worst
         of the three outcomes. */
      var pid = t.getAttribute('data-panel');
      if(!document.getElementById(pid)){
        var hrf = t.getAttribute('href');
        if(t.tagName === 'A' && hrf && hrf !== '#') return;
        e.preventDefault();
        return;
      }
      e.preventDefault();
      show(pid, t);
      return;
    }
    if(e.target.closest('[data-close]') || e.target === scrim) close(false);
  });
  document.addEventListener('keydown', function(e){
    if(e.key === 'Escape' && open) close(false);
  });
  /* keep tabbing inside the open panel */
  document.addEventListener('focusin', function(e){
    /* The floating bag and saved buttons sit above the panel on purpose,
       so they are part of what is reachable while it is open — pulling
       focus off them would make them look broken to anyone tabbing. */
    if(e.target.closest && e.target.closest('.fabs')) return;
    if(open && !open.contains(e.target) && !scrim.contains(e.target)){
      var f = open.querySelector('.p-x');
      if(f) f.focus();
    }
  });
  window.__closePanel = function(){ close(false); };
  window.__openPanel = function(id){ show(id, null); };
})();

/* ═══════════ DEAD IN-PAGE LINKS ═══════════
   The menu is edited in Shopify, the sections are edited in the theme
   editor, and neither knows about the other. Switch a section off — or
   merge two, as Made and About were merged — and any menu item pointing
   at its anchor becomes a link that scrolls nowhere, which reads as a
   broken site rather than a changed one.

   So on the homepage only, a nav link to an anchor that is not on the
   page is removed. Links to real pages are never touched. */
(function () {
  if (!document.body.classList.contains('tpl-index')) return;
  /* The footer carries the same menus, so it inherits the same dead
     anchors — and the footer is this site's main internal-linking
     surface, which is the worst place to leave links that go nowhere. */
  var links = document.querySelectorAll('nav a[href^="#"], #mnav a[href^="#"], footer a[href^="#"]');
  Array.prototype.forEach.call(links, function (a) {
    var id = a.getAttribute('href').slice(1);
    if (!id || document.getElementById(id)) return;
    var li = a.closest('li');
    (li || a).remove();
  });
})();

/* ═══════════ ACCORDION ═══════════ */
function toggleAcc(h){
  var item = h.parentElement, open = item.classList.contains('open');
  /* Close only the siblings in THIS accordion. A dropdown in front of the
     film and an accordion inside a panel are two different lists, and
     opening one should not silently collapse the other. */
  var box = item.parentElement || document;
  box.querySelectorAll('.acc-i.open').forEach(function(i){
    i.classList.remove('open');
    i.querySelector('.acc-b').style.maxHeight = '';
    var hd = i.querySelector('.acc-h'); if(hd) hd.setAttribute('aria-expanded','false');
  });
  if(!open){
    item.classList.add('open');
    h.setAttribute('aria-expanded','true');
    /* Measure rather than guess. A fixed max-height in the stylesheet is a
       number someone has to keep in step with the content, and the day it
       falls behind the row is silently cut off — which is exactly what
       twelve products did at phone width. */
    setAccHeight(item);
  }
  /* the row's height animates, so re-measure the page as it settles */
  if(window.__remeasure){
    var n = 0, t = setInterval(function(){
      window.__remeasure();
      if(++n > 10) clearInterval(t);
    }, 60);
  }
}

function setAccHeight(item){
  var body = item.querySelector('.acc-b'), inner = body.firstElementChild;
  body.style.maxHeight = ((inner ? inner.offsetHeight : body.scrollHeight) + 4) + 'px';
}

/* A wrapped row is a different height at a different width, so an open
   accordion is re-measured whenever the page is. */
addEventListener('resize', function(){
  document.querySelectorAll('.acc-i.open').forEach(setAccHeight);
});


/* ═══════════ BLOG MODALS ═══════════ */
function openModal(id){
  document.getElementById(id).classList.add('open');
  document.body.style.overflow = 'hidden';
}
function closeModal(id){
  document.getElementById(id).classList.remove('open');
  document.body.style.overflow = '';
}
function bgClose(e, id){ if(e.target === document.getElementById(id)) closeModal(id); }
document.addEventListener('keydown', function(e){
  if(e.key !== 'Escape') return;
  document.querySelectorAll('.mov.open').forEach(function(m){ m.classList.remove('open'); });
  document.body.style.overflow = '';
});

/* ═══════════════════════════════════════════════════════════════
   CONTACT FORM — posts to the Shopify contact endpoint at
   bustah.co/contact, exactly as the store's own contact page does
   (form_type=contact, contact[name], contact[email], contact[body]).

   It submits natively rather than in the background. A background
   fetch would be a cross-origin POST to a different domain and the
   browser would hide the response from us, so we could not tell a
   delivered message from a rejected one — the visitor would be shown
   a thank-you that might be a lie. A native submit hands them
   Shopify's own confirmation instead, which is the truthful one.

   Works with JavaScript off: the browser submits the same form the
   same way. All this module adds is catching the three mistakes
   people actually make before the page navigates.
   ═══════════════════════════════════════════════════════════════ */
(function(){
  var form = document.querySelector('.p-form');
  if(!form) return;

  var err = document.createElement('p');
  err.className = 'ferr';
  err.hidden = true;
  err.setAttribute('role', 'alert');
  form.insertBefore(err, form.querySelector('button[type="submit"]'));

  function fail(e, msg, field){
    e.preventDefault();
    err.textContent = msg; err.hidden = false;
    form.querySelectorAll('.fg.bad').forEach(function(g){ g.classList.remove('bad'); });
    if(field){
      var g = field.closest('.fg');
      if(g) g.classList.add('bad');
      field.focus();
    }
    return false;
  }

  form.addEventListener('submit', function(e){
    err.hidden = true;
    var name  = form.elements['contact[name]'],
        email = form.elements['contact[email]'],
        body  = form.elements['contact[body]'];

    if(!name.value.trim())
      return fail(e, 'Please tell us your name.', name);
    if(!/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(email.value.trim()))
      return fail(e, 'That email address does not look right.', email);
    if(body.value.trim().length < 10)
      return fail(e, 'Please add a little detail about what you are after.', body);

    var btn = form.querySelector('button[type="submit"]');
    if(btn){ btn.disabled = true; btn.textContent = 'Sending…'; }
  });
})();
/* ═══════════ THE BAR AT THE BOTTOM OF THE WINDOW ═══════════
   Shopify docks its theme preview bar to the bottom of the viewport
   whenever a draft theme is being looked at — 68px tall, full width, and
   an <iframe>, which means it takes the click at any point it covers no
   matter what z-index our own buttons carry. The lowest button in the
   floating stack is the bag, so the bag is the one that "does nothing"
   while a theme is being reviewed: it is under the bar, not broken.

   The same is true of a cookie banner or an app's sticky bar on the live
   store. So the height of whatever is docked down there is measured and
   published as --botbar; the stylesheet lifts the buttons by it. Nothing
   is docked for an ordinary visitor, --botbar is 0, and the design is
   exactly as drawn. */
(function(){
  var SEL = '#PBarNextFrameWrapper,#preview-bar-iframe,#admin-bar-iframe';
  function measure(){
    var h = 0;
    [].slice.call(document.querySelectorAll(SEL)).forEach(function(el){
      var cs = getComputedStyle(el);
      if(cs.display === 'none' || cs.visibility === 'hidden') return;
      var r = el.getBoundingClientRect();
      /* only a bar actually sitting on the bottom edge of the window */
      if(r.height && r.bottom >= window.innerHeight - 2) h = Math.max(h, r.height);
    });
    document.documentElement.style.setProperty('--botbar', h ? h + 'px' : '0px');
  }
  measure();
  window.addEventListener('resize', measure);
  /* the bar is injected after the page, and can take a moment to size */
  [300, 900, 2000, 4000].forEach(function(ms){ setTimeout(measure, ms); });
  if(window.MutationObserver){
    new MutationObserver(measure).observe(document.body, { childList: true });
  }
})();
